import { competitorMediaEffectQueryOptions } from '@/dashboards/CompetitorsMediaInvestment/api';
import { getCompetitorEffectVariables } from '@/dashboards/CompetitorsMediaInvestment/helpers/getCompetitorEffectVariables';
import { useExecutiveSummaryContext } from '@/dashboards/ExecutiveSummary/context';
import {
    calculateCompetitorImpactTotals,
    type CompetitorImpactTotals,
} from '@/dashboards/ExecutiveSummary/helpers/calculateCompetitorImpact';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export type { CompetitorImpactTotals };

export interface CompetitorImpactData extends CompetitorImpactTotals {}

export interface UseCompetitorImpactQueryResult {
    isLoading: boolean;
    isFetching: boolean;
    data: CompetitorImpactData;
}

/**
 * Competitor Impact — sum of the modeled competitor media effect (contribution
 * of competitor variables) over the selected period.
 *
 * Reuses `competitorMediaEffectQueryOptions` (the same source the Competitors
 * Media Investment dashboard reads from), which filters by date in SQL, so we
 * simply run it once for the primary range and once for the comparison range
 * and sum the returned rows.
 */
export const useCompetitorImpactQuery = (): UseCompetitorImpactQueryResult => {
    const { primaryRange, comparisonRange } = useExecutiveSummaryContext();
    const {
        dashboard: { dashboardVariables },
    } = useDashboardGlobalContext();

    const competitorImpactVariables = useMemo(
        () => getCompetitorEffectVariables(dashboardVariables),
        [dashboardVariables],
    );

    const hasCompetitorVariables = competitorImpactVariables.length > 0;

    const buildQueryOptions = (range?: {
        startDate?: string;
        endDate?: string;
    }) => {
        const hasRange = Boolean(range?.startDate && range?.endDate);
        return {
            ...competitorMediaEffectQueryOptions({
                competitorImpactVariables,
                period: 'yearly',
                startDate: range?.startDate,
                endDate: range?.endDate,
            }),
            enabled: hasCompetitorVariables && hasRange,
        };
    };

    const primaryQueryResult = useQuery(buildQueryOptions(primaryRange));
    const comparisonQueryResult = useQuery(buildQueryOptions(comparisonRange));

    const data = useMemo<CompetitorImpactData>(
        () =>
            calculateCompetitorImpactTotals(
                primaryQueryResult.data?.rows,
                comparisonQueryResult.data?.rows,
            ),
        [primaryQueryResult.data?.rows, comparisonQueryResult.data?.rows],
    );

    return {
        isLoading:
            primaryQueryResult.isLoading || comparisonQueryResult.isLoading,
        isFetching:
            primaryQueryResult.isFetching || comparisonQueryResult.isFetching,
        data,
    };
};